/**
 * Utility functions for handling events in the Rugby Event Tracker
 */
import { getFieldZone } from '../constants/rugbyConstants';

/**
 * Creates a new event based on user interaction and form data
 *
 * @param {Object} pendingEvent - Data about position and timestamp
 * @param {string} eventType - Type of the event (e.g., Try, Tackle)
 * @param {string} team - Team identifier (Home/Away)
 * @param {string} playerNumber - Player's number
 * @param {string} comment - Optional comment
 * @param {string} matchPeriod - Current match period
 * @param {string} teamDirection - Team's initial playing direction
 * @returns {Object} Complete event object
 */
export const createEvent = (pendingEvent, eventType, team, playerNumber, comment, matchPeriod, teamDirection = 'left-to-right') => {
    // Calculate which zone this event occurred in, accounting for period and team direction
    const zone = getFieldZone(pendingEvent.position.x, matchPeriod, teamDirection);
    
    return {
        ...pendingEvent,
        eventType,
        team,
        playerNumber,
        comment: comment.trim() || null,
        period: matchPeriod,
        zone: zone // Add the zone to the event data
    };
};

/**
 * Updates an existing event with new data
 *
 * @param {Array} events - Current events array
 * @param {number} eventId - ID of the event to update
 * @param {string} eventType - Type of the event
 * @param {string} team - Team identifier
 * @param {string} playerNumber - Number of the player
 * @param {string} comment - Optional comment
 * @param {string} matchPeriod - Current match period
 * @param {string} teamDirection - Team's initial playing direction
 * @returns {Array} Updated events array
 */
export const updateEvent = (events, eventId, eventType, team, playerNumber, comment, matchPeriod, teamDirection = 'left-to-right') => {
    return events.map(event => {
        if (event.id === eventId) {
            const updatedEvent = {
                ...event,
                eventType,
                team,
                playerNumber,
                comment: comment.trim() || null,
                period: matchPeriod
            };
            
            // Recalculate zone if it doesn't exist or if period changes (which affects zone interpretation)
            if (!event.zone || event.period !== matchPeriod) {
                updatedEvent.zone = getFieldZone(event.position.x, matchPeriod, teamDirection);
            }
            
            return updatedEvent;
        }
        return event;
    });
};

/**
 * Removes an event from the events array
 * 
 * @param {Array} events - Current events array
 * @param {number} eventId - ID of the event to delete
 * @returns {Array} Updated events array
 */
export const deleteEvent = (events, eventId) => {
    return events.filter(event => event.id !== eventId);
};

/**
 * Checks if an event is a duplicate (same position within a short time)
 * 
 * @param {Array} events - Current events array
 * @param {Object} position - Position of the new event {x, y}
 * @param {number} seconds - Current timer seconds
 * @param {string} eventType - Type of the event
 * @param {string} team - Team identifier
 * @returns {boolean} True if duplicate is detected
 */
export const checkDuplicateEvent = (events, position, seconds, eventType, team) => {
    // Don't check if there are no events
    if (!events.length) return false;
    
    // Get the latest event
    const latestEvent = events[events.length - 1];
    
    // Define thresholds for position and time
    // Marker size is about 24px, field is displayed with varying dimensions
    // We'll use 1.5x marker size as threshold in relative coordinates
    const markerSizeRelative = 0.005; // ~0.5% of field width/height (approximately the marker radius)
    const timeThreshold = 3; // 3 seconds
    
    // Calculate position distance
    const distance = Math.sqrt(
        Math.pow(position.x - latestEvent.position.x, 2) + 
        Math.pow(position.y - latestEvent.position.y, 2)
    );
    
    // Calculate time difference
    const timeDiff = Math.abs(seconds - latestEvent.seconds);
    
    // Check if this is a duplicate click (same position, same team, same event type, within time threshold)
    return (
        distance < markerSizeRelative && 
        timeDiff < timeThreshold &&
        team === latestEvent.team &&
        eventType === latestEvent.eventType
    );
};

/**
 * Filters events based on provided criteria
 *
 * @param {Array} events - Array of events to filter
 * @param {Object} filters - Filter criteria
 * @param {string} teamDirection - Team's initial playing direction
 * @returns {Array} Filtered events
 */
export const filterEvents = (events, filters, teamDirection = 'left-to-right') => {
    return events.filter(event => {
        if (filters.eventType && event.eventType !== filters.eventType) return false;
        if (filters.team && event.team !== filters.team) return false;
        if (filters.player && event.playerNumber !== filters.player) return false;
        if (filters.period && event.period !== filters.period) return false;
        if (filters.zone) {
            const eventZone = event.zone || getFieldZone(event.position.x, event.period, teamDirection);
            if (eventZone !== filters.zone) return false;
        }
        return true;
    });
};

/**
 * Converts a position from screen coordinates to relative field position
 * Improved to handle different sidebar states and ensure accurate marker placement
 * 
 * @param {Object} clickEvent - Mouse click event
 * @param {Object} fieldElement - Reference to the field container element
 * @returns {Object} Position as {x, y} with values between 0 and 1
 */
export const getFieldPosition = (clickEvent, fieldElement) => {
    if (!fieldElement) {
        console.error('Field element is not defined');
        return { x: 0, y: 0 };
    }

    // Get the current bounds of the container
    const rect = fieldElement.getBoundingClientRect();

    // Calculate precise relative position within the container (0-1 range)
    // This ensures markers appear exactly where clicked regardless of container size
    const x = Math.min(Math.max((clickEvent.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((clickEvent.clientY - rect.top) / rect.height, 0), 1);

    return { x, y };
};

/**
 * Calculate marker size based on screen dimensions for responsive design
 * 
 * @returns {Object} Size information for markers (width, height, fontSize)
 */
export const getMarkerSize = () => {
    if (typeof window !== 'undefined') {
        const screenWidth = window.innerWidth;
        if (screenWidth <= 480) return { width: 20, height: 20, fontSize: 10 };
        if (screenWidth <= 768) return { width: 24, height: 24, fontSize: 12 };
        return { width: 30, height: 30, fontSize: 14 };
    }
    return { width: 30, height: 30, fontSize: 14 }; // Default for SSR
};

/**
 * Get player information by number and team
 * 
 * @param {string} playerNumber - Player's number
 * @param {string} team - Team identifier (Home/Away)
 * @param {Array} homeTeamPlayers - Home team players array
 * @param {Array} awayTeamPlayers - Away team players array
 * @returns {Object|null} Player object or null if not found
 */
export const getPlayerInfo = (playerNumber, team, homeTeamPlayers, awayTeamPlayers) => {
    const players = team === 'Home' ? homeTeamPlayers : awayTeamPlayers;
    return players.find(p => p.number === parseInt(playerNumber)) || null;
};

/**
 * Get events for a specific player
 * 
 * @param {Array} events - All events
 * @param {string} playerNumber - Player's number
 * @param {string} team - Team identifier (Home/Away)
 * @returns {Array} Events involving the specified player
 */
export const getPlayerEvents = (events, playerNumber, team) => {
    return events.filter(event =>
        event.playerNumber === playerNumber && event.team === team
    );
};

/**
 * Get events in a specific zone
 *
 * @param {Array} events - All events
 * @param {string} zoneName - Zone name (Red Zone, Yellow Zone, Green Zone)
 * @param {string} teamDirection - Team's initial playing direction
 * @returns {Array} Events in the specified zone
 */
export const getEventsInZone = (events, zoneName, teamDirection = 'left-to-right') => {
    return events.filter(event => {
        const eventZone = event.zone || getFieldZone(event.position.x, event.period, teamDirection);
        return eventZone === zoneName;
    });
};
/**
 * Get zone statistics for events
 *
 * @param {Array} events - All events
 * @param {string} teamDirection - Team's initial playing direction
 * @returns {Object} Zone statistics
 */
export const getZoneStats = (events, teamDirection = 'left-to-right') => {
    const stats = {
        'Red Zone': { home: 0, away: 0, total: 0 },
        'Yellow Zone': { home: 0, away: 0, total: 0 },
        'Green Zone': { home: 0, away: 0, total: 0 }
    };
    
    events.forEach(event => {
        const zone = event.zone || getFieldZone(event.position.x, event.period, teamDirection);
        if (stats[zone]) {
            stats[zone].total++;
            if (event.team === 'Home') {
                stats[zone].home++;
            } else {
                stats[zone].away++;
            }
        }
    });
    
    // Calculate percentages
    Object.keys(stats).forEach(zone => {
        const zoneTotal = stats[zone].total;
        if (zoneTotal > 0) {
            stats[zone].homePercentage = Math.round((stats[zone].home / zoneTotal) * 100);
            stats[zone].awayPercentage = Math.round((stats[zone].away / zoneTotal) * 100);
        } else {
            stats[zone].homePercentage = 0;
            stats[zone].awayPercentage = 0;
        }
    });
    
    return stats;
};  